// ─── Carpetas del helpdesk ────────────────────────────────────────────────────
// CRUD de carpetas (colección helpdesk_folders del microservicio conversations)
// y asignación de carpeta a una conversación. Mismo patrón de headers que el
// resto del helpdesk (fetchChannelAccounts / aiChatService): accessToken de
// (vía authFetch) + x-app/application.
//
// Los errores se lanzan con `error.status` poblado: el 409 es semántico acá
// (nombre duplicado o tope de 30 carpetas) y la UI lo muestra inline en vez de
// tratarlo como un fallo genérico.

import { authFetch } from "./authFetch";

// REACT_APP_API_CONNECTION ya incluye /api/v1/ al final; se recorta para poder
// armar paths completos sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(
  /\/api\/v1\/?$/,
  ""
);

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch.
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

async function request(path, options = {}) {
  const response = await authFetch(`${BFF_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...buildAuthHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // message del backend primero: el 409 distingue "nombre duplicado" de
    // "máximo 30 carpetas" solo por el texto.
    const error = new Error(
      data?.message || data?.detail || `Error ${response.status}`
    );
    error.status = response.status;
    throw error;
  }
  return data;
}

/**
 * Lista las carpetas del tenant (con `count` de conversaciones no resueltas).
 * @returns {Promise<Array<{id:string,name:string,color:string|null,position:number,created_by:string,count:number,createdAt:string,updatedAt:string}>>}
 */
export async function getFolders() {
  const data = await request("/api/v1/helpdesk/folders");
  return data.folders ?? [];
}

/**
 * Crea una carpeta. 409 = nombre duplicado o tope de 30 carpetas.
 * @param {{name:string, color?:string}} payload
 */
export async function createFolder({ name, color }) {
  const data = await request("/api/v1/helpdesk/folders", {
    method: "POST",
    body: JSON.stringify({ name, ...(color ? { color } : {}) }),
  });
  return data.folder ?? data;
}

/**
 * Renombra / recolorea una carpeta. 409 = nombre duplicado.
 * @param {string} id
 * @param {{name?:string, color?:string}} payload
 */
export async function updateFolder(id, { name, color }) {
  const data = await request(`/api/v1/helpdesk/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      ...(name !== undefined ? { name } : {}),
      ...(color !== undefined ? { color } : {}),
    }),
  });
  return data.folder ?? data;
}

/**
 * Elimina una carpeta. Las conversaciones NO se borran: el backend las
 * desasigna (folder_id=null) y devuelve cuántas quedaron sin carpeta.
 * @param {string} id
 * @returns {Promise<{deleted:boolean, unfiled_count:number}>}
 */
export async function deleteFolder(id) {
  const data = await request(`/api/v1/helpdesk/folders/${id}`, {
    method: "DELETE",
  });
  return { deleted: data.deleted ?? true, unfiled_count: data.unfiled_count ?? 0 };
}

/**
 * Mueve una conversación a una carpeta (o la saca de todas con null).
 * @param {string} conversationId
 * @param {string|null} folderId
 * @returns {Promise<object>} la conversación actualizada
 */
export async function setConversationFolder(conversationId, folderId) {
  const data = await request(`/api/v1/conversations/${conversationId}/folder`, {
    method: "PATCH",
    body: JSON.stringify({ folder_id: folderId }),
  });
  return data.conversation ?? data;
}
